import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';
import { getRandomColor } from '@utils/getRandomColor';
import { ClinicalTrialAffiliatesType } from '@dynamika-shared/types/clinicalTrialAffiliates';
import ClinicalTrialListLoading from '@components/LoginSelections/ClinicalTrialList/ClinicalTrialList.loading';
import { FC } from 'react';
import { twJoin } from 'tailwind-merge';
import { twMerge } from '@utils/twMerge';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'antd';

type ClinicalTrialListProps = {
  clinicalTrials: ClinicalTrialAffiliatesType[];
  handleSelectCT: (clinicalTrial: ClinicalTrialAffiliatesType) => void;
  context?: 'menu' | 'selectCT';
  loading?: boolean;
  classNameButton?: string;
  classNameButtonContainer?: string;
  sidebarCollapsed?: boolean;
};

export const ClinicalTrialList: FC<ClinicalTrialListProps> = ({
  clinicalTrials = [],
  handleSelectCT,
  context,
  loading,
  classNameButton,
  classNameButtonContainer,
  sidebarCollapsed,
}) => {
  const { clinicalTrialId, id: urlId } = useParams<{
    clinicalTrialId?: string;
    id?: string;
  }>();

  const resolvedTrialId = clinicalTrialId || urlId;
  if (loading) {
    return <ClinicalTrialListLoading count={3} context={context} />;
  }

  const isMenu = context === 'menu';

  return (
    <>
      {isMenu && !sidebarCollapsed && (
        <div className='text-1016 text-dimGray mx-3 my-2 mb-2'>
          Clinical Trials
        </div>
      )}
      <div className={twJoin(isMenu && 'max-h-[55vh]')}>
        {[...(clinicalTrials || [])]
          .sort((a, b) => {
            const aName = a.name.toLowerCase();
            const bName = b.name.toLowerCase();

            if (aName === bName) {
              return 0;
            }

            return aName < bName ? -1 : 1;
          })
          ?.map((clinicalTrial, i) => {
            const { name, id } = clinicalTrial;
            const active = resolvedTrialId === id ? 'bg-waterLight' : '';
            const rounded = isMenu ? ' rounded-[12px]' : 'rounded-2xl ';

            const randomColor = getRandomColor(i);

            return (
              <Tooltip
                color='black'
                title={<div className='text-white'>{name}</div>}
                placement='right'
                mouseEnterDelay={0.5}
                key={id}
              >
                <div className={twMerge(classNameButtonContainer)}>
                  {context === 'selectCT' && (
                    <div className='border-brightGray mx-auto my-0 w-[95%] border-t-[1px]'></div>
                  )}
                  <div
                    className={twMerge(
                      active,
                      rounded,
                      'hover:bg-waterLightHover50 flex w-full cursor-pointer items-center justify-between p-1',
                      isMenu ? 'h-6' : 'h-9',
                      classNameButton
                    )}
                    onClick={() => handleSelectCT(clinicalTrial)}
                  >
                    <div className='flex items-center overflow-hidden'>
                      <p
                        className={twJoin(
                          'text-1824 mr-1 flex items-center justify-center rounded-full p-2 font-semibold text-black',
                          isMenu ? 'h-[32px] w-[32px] ' : 'h-[40px] w-[40px]',
                          randomColor
                        )}
                      >
                        {name?.charAt(0)?.toUpperCase()}
                      </p>
                      {!sidebarCollapsed && (
                        <p
                          className={twJoin(
                            'truncate text-black',
                            isMenu ? 'text-[12px]' : 'text-[16px] font-semibold'
                          )}
                        >
                          {name}
                        </p>
                      )}
                    </div>

                    {context === 'selectCT' && (
                      <div className='pr-2'>
                        <ChevronIcon
                          dir={'right'}
                          className='text-spanishGray h-3 w-3 '
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Tooltip>
            );
          })}
      </div>
    </>
  );
};
